<!--
    Component Tagcloud
    @author Leander Eyer <leander.eyer@elead.ch>
-->

<template>
	<div class="container">
		<div id='tagcloud-content' class="content" :style="{ display: cursor < 0? 'block' : 'none' }">

			<!-- First Row: Usability hint or selected Tags -->
			<div v-if="activeTags.length > 0" class="darkText largeText tagcloud-tags">
				<div
					v-for="tag in activeTags"
					class="tagcloud-tag bg-tagcloud-light link"
					:key="tag"
					@click="removeTag(tag)">
					{{ tag }}
					<span class="tagcloud-close">x</span>
				</div>
			</div>
			<div v-else class="darkText largeText tagcloud-hint bg-tagcloud-light">
				{{ model.getDictionary('tag_hint') }}
			</div>

			<!-- Second Row: Tag Cloud -->
			<div id="tagcloud-canvas" class="tagcloud-canvas"></div>

			<template v-if="activeTags.length > 0">
				<div class="darkText largeText tagcloud-work link bg-work">{{list.length}} Werke gefunden</div>

				<div class="tagcloud-grid" 
					:style="{ visibility: activeTags.length > 0? 'visible': 'hidden' }"
					@wheel.prevent="scroll($event.deltaY > 0? 1 : -1)">
					<div
						class="tagcloud-image zoomable" 
						v-for="(work, index) in list.slice(slice, slice + this.sliceSize)"
						:key="work.getID()"
						:work="work"
						:index="index">
							<img v-if="work.getImages().isImage()"
								class="link"
								:src="work.getImages().getImageURL(previewImageQuality)"
								:title="work.getArtistLabel() + ' - ' + work.getTitle() + ' (' + work.getYear() + ')'"
								@click="select(index + slice)"
							/>
							<div v-else
								class="link"
								v-html="work.getImages().getMultiMediaHTML(0, 'preview')"
								:title="work.getArtistLabel() + ' - ' + work.getTitle() + ' (' + work.getYear() + ')'"
								@click="select(index + slice)"></div>
					</div>

					<div
						class="tagcloud-grid-left arrow"
						v-if="slice > 0"
						@click.left="scroll(-1)"
						@mousedown.left="autoscroll('start', -1)"
						@mouseup.left="autoscroll('stop')"
					>&#x25C0;</div>
					<div
						class="tagcloud-grid-right arrow"
						v-if="slice < maxSlice"
						@click.left="scroll(1)"
						@mousedown.left="autoscroll('start', 1)"
						@mouseup.left="autoscroll('stop')"
					>&#x25B6;</div>
				</div>
			</template>
		</div>

		<work-detail v-if="cursor >= 0" :list="list" :index="cursor"></work-detail>
	</div>
</template>

<script>

	import Vue from "vue";
	import { GRID_COLUMNS } from "./../model/config.js";
	import { model } from "./../model/Model.js";
	import { ListWithCursor } from "./../mixin/ListWithCursor.js";
	import { Scrollable } from "./../mixin/Scrollable.js";
	import WorkDetail from "./WorkDetail.vue";
	import { tagToID } from "./../util.js";

	export default {
		mixins: [ListWithCursor, Scrollable],
		components: { WorkDetail },

		data: function() {
			return {
				activeTags: [],
				tags: model.getTags().getList(),
				mode: "cloud",

				// source of the preview images
				previewImageQuality: 'small',
				// number of cells visible at once
				sliceSize: GRID_COLUMNS,

				// make the datamodel accessible
				model: model
			};
		},

		computed: {
			lang: function() {	return this.model.getLang();	}
		},

		methods: {
			removeTag: function(tag) {
				let index = this.activeTags.indexOf(tag);
				this.activeTags.splice(index, 1);
			},

			render: function() {
				/* global d3 */
				let data = [];
				for (let i = 0; i < this.tags.length; i++) {
					if (this.tags[i].getTaggedWork().length > 0) {

						// supress "oberwalliser*in"
						if (this.tags [i].getID() == 87) {
							continue;
						}
						
						data.push({
							text: this.tags[i].getLabel(),
							id: this.tags[i].getID(),
							count: this.tags[i].getTaggedWork().length,
							size: 21 + 1.1 * Math.pow(this.tags[i].getTaggedWork().length, 1/3)
						});
					}
				}

				this.activeTags = [];
				document.getElementById("tagcloud-canvas").innerHTML = '';
				var width = document.getElementById("tagcloud-canvas").getBoundingClientRect().width;
				var height = document.getElementById("tagcloud-canvas").getBoundingClientRect().height;
				var fill = d3.scale.category20();

				// prepare positioning
				d3.layout
					.cloud()
					.size([width, height])
					.words(data)
					.rotate(function() {
						return 0;
					})
					.font("Sans-Serif")
					.fontSize(function(d) {
						return d.size;
					})
					.on("end", drawSkillCloud)
					.start();

				// render the actual tag cloud
				let activeTagsClosure = this.activeTags;
				function drawSkillCloud(words) {
					d3.select("#tagcloud-canvas")
						.append("svg")
						.attr("width", width)
						.attr("height", height)
						.append("g")
						.attr(
							"transform",
							"translate(" + ~~(width / 2) + "," + ~~(height / 2) + ")"
						)
						.selectAll("text")
						.data(words)
						.enter()
						.append("text")
						.attr("id", function(d) {
							return tagToID(d.text);
						})
						.attr("title", function(d) {
							return d.text + ": " + d.count + " Werke";
						})
						.on("click", function(d) {
							if (activeTagsClosure.indexOf(d.text) == -1) {
								activeTagsClosure.push(d.text);
							}
						})
						.style("font-size", function(d) {
							return d.size - 10 + "px";
						})
						.style("cursor", "pointer")
						.style("font-family", "Sans-Serif")
						.style("fill", function(d, i) {
							return fill(i);
						})
						.attr("text-anchor", "middle")
						.attr("transform", function(d) {
							return (
								"translate(" + [d.x, d.y] + ")rotate(" + d.rotate + ")"
							);
						})
						.text(function(d) {
							return d.text;
						});
				}

				// set the viewbox to content bounding box (zooming in on the content, effectively trimming whitespace)
				var svg = document.getElementsByTagName("svg")[0];
				var bbox = svg.getBBox();
				var viewBox = [bbox.x, bbox.y, bbox.width, bbox.height].join(" ");
				svg.setAttribute("viewBox", viewBox);
			}
		},

		watch: {

			lang: function() {
				this.render();
			},

			// Update the list of visible tags and works
			activeTags: function() {
				// build a list of works that have the requested tags
				let filteredList = [];
				let candidates = model.getWorks().getList().filter(function(candidate) {
				return candidate.getFlagHideInArtistList() == false;
				});
				for (let i = 0; i < candidates.length; i++) {
					let pass = true;

					for (let j = 0; j < this.activeTags.length; j++) {
						if (candidates[i].hasTag(this.activeTags[j]) == false) {
							pass = false;
							break;
						}
					}

					if (pass) {
						filteredList.push(candidates[i]);
					}
				}
				this.list = filteredList;
				this.maxSlice = Math.max(0, this.list.length - this.sliceSize);
				this.slice = 0;

				// hide all tags that can't be added to the selection without creating an emtpy list
				for (let i = 0; i < this.tags.length; i++) {
					let tag = this.tags[i].getLabel();

					// only tags on works are considered
					if (this.tags[i].getTaggedWork().length == 0) {
						continue;
					}

					// filtered tags are always hidden
					if (this.activeTags.indexOf(tag) != -1) {
						let node = document.getElementById(tagToID(tag));
						if (typeof node !== "undefined" && node != null) {
							node.style.display = "none";
						}
						continue;
					}

					// the tag must appear in at least one of the remaining works
					let inRemaining = false;
					for (let j = 0; j < this.list.length && inRemaining == false; j++) {
						if (this.list[j].hasTag(tag)) {
							inRemaining = true;
						}
					}
					if (inRemaining == false) {
						let node = document.getElementById(tagToID(tag));
						if (typeof node !== "undefined" && node != null) {
							node.style.display = "none";
						}
						continue;
					}

					// passed all tests, show it
					let node = document.getElementById(tagToID(tag));
					if (typeof node !== "undefined" && node != null) {
						node.style.display = "block";
					}
				}

			}
		},

		// created: initialize the list
		created: function() {
			this.list = model.getWorks().getList().filter(function(candidate) {
				return candidate.getFlagHideInArtistList() == false;
			});
			this.sliceSize = Vue.prototype.$device == 'large'? GRID_COLUMNS : 3;
			this.maxSlice = Math.max(0, this.list.length - this.sliceSize);
			this.scrollBy = 1;
			this.previewImageQuality = Vue.prototype.$previewImageQuality;
		},

		// build the tag cloud
		mounted: function() {
			this.render();
		}
	};
</script>


<style>

@media (min-width: 1000px) {

	/*	Styles for the Tag Cloud Header Row */
	div.tagcloud-hint {
		padding: 0.4rem 1rem 0.4rem 1rem;
		width: 64rem;
		margin: 0 2rem 0 0;		
	}

	div.tagcloud-tags {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: flex-start;

		width: 64rem;
		margin: 0 2rem 0 0;		
	}

	div.tagcloud-tag {
		position: relative;
		margin: 0 1rem 1rem 0;
		padding: 0.4rem 2rem 0.4rem 1rem;
		border-radius: 0.5rem;
	}

	span.tagcloud-close {
		display: block;
		position: absolute;
		top: 0.5rem;
		right: 0.75rem;
		font-size: 1rem;
	}

	/*	Styles for the Tag Cloud Body Row */
	div.tagcloud-canvas {
		margin: 2rem 0;
		width: 64rem;
		height: 31rem;
	}

	/*	Styles for the Works Header Row */
	div.tagcloud-work {
		width: 20rem;
		padding: 0.4rem 1rem 0.4rem 1rem;
	}

	/*	Styles for the Works Grid Row */
	div.tagcloud-grid {
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;

		justify-content: flex-start;
		align-items: flex-start;

		margin-top: 2rem;
		width: 66rem;
	}

	div.tagcloud-image {
		width: 9rem;
		height: 9rem;
		margin: 0 2rem 2rem 0;
		overflow: hidden;
	}

	div.tagcloud-image img {
		object-fit: cover;
		object-position: center;
		width: 9rem;
		height: 9rem;
	}

	div.tagcloud-grid-left {
		position: absolute;
		top: 3.5rem;
		left: -3rem;
		cursor: pointer;
		user-select: none;
	}

	div.tagcloud-grid-right {
		position: absolute;
		top: 3.5rem;
		right: -1rem;
		cursor: pointer;
		user-select: none;
	}	

}

@media (max-width: 1000px) {

	/*	Styles for the Tag Cloud Header Row */
	div.tagcloud-hint {
		padding: 0.4rem 1rem 0.4rem 1rem;
		width: 100%;
		margin: 0 2rem 0 0;		
	}

	div.tagcloud-tags {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		justify-content: flex-start;

		width: 100%;
		margin: 0 2rem 0 0;		
		overflow: scroll;
	}

	div.tagcloud-tag {
		position: relative;
		margin: 0 1rem 1rem 0;
		padding: 0.4rem 2rem 0.4rem 1rem;
		border-radius: 0.5rem;
	}

	span.tagcloud-close {
		display: block;
		position: absolute;
		top: 0.5rem;
		right: 0.75rem;
		font-size: 1rem;
	}

	/*	Styles for the Tag Cloud Body Row */
	div.tagcloud-canvas {
		margin: 2rem 0;
		width: 100%;
		height: calc(100% - 22rem);
	}

	/*	Styles for the Works Header Row */
	div.tagcloud-work {
		width: 20rem;
		padding: 0.4rem 1rem 0.4rem 1rem;
	}

	/*	Styles for the Works Grid Row */
	div.tagcloud-grid {
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;

		justify-content: flex-start;
		align-items: flex-start;

		margin-top: 2rem;
		width: 100%;
		height: 9rem;
	}

	div.tagcloud-image {
		flex-grow: 1;
		height: 9rem;
		width: calc(33% - 2rem);
		max-width: calc(33% - 2rem);
		margin: 0 2rem 2rem 0;
		overflow: hidden;
	}

	div.tagcloud-image img, div.tagcloud-image div {
		object-fit: cover;
		object-position: center;
		width: 100%;
		height: 100%;
	}	

	div.tagcloud-grid-left {
		position: absolute;
		top: 3.5rem;
		left: -3rem;
		cursor: pointer;
		user-select: none;
	}

	div.tagcloud-grid-right {
		position: absolute;
		top: 3.5rem;
		right: -1rem;
		cursor: pointer;
		user-select: none;
	}	

}

</style>